"use client";

import { useEffect, useMemo, useState } from "react";

import { motion } from "framer-motion";
import { Check, Copy, ExternalLink } from "lucide-react";

import { EnrichmentRenderer } from "@/components/debug/EnrichmentRenderer";
import { ProductAttributesRenderer } from "@/components/debug/ProductAttributesRenderer";
import { useAuth } from "@/contexts/AuthContext";
import { isInternalUser } from "@/lib/internal-user";
import type { Product } from "@/lib/product-utils";
import { hydrateProductByUuid } from "@/lib/shop-agent-client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type DebugTab = "enrichment" | "product";

const DEBUG_TABS: { id: DebugTab; label: string }[] = [
  { id: "enrichment", label: "Enrichment" },
  { id: "product", label: "Product" },
];

// ---------------------------------------------------------------------------
// Hook: fetch the full product from the authenticated catalog endpoint so the
// debug panel always has every field (the PDP page data may be stripped down).
// ---------------------------------------------------------------------------

function useFullProduct(product: Product): {
  fullProduct: Record<string, unknown>;
  loading: boolean;
  hydrationFailed: boolean;
} {
  const [hydratedRaw, setHydratedRaw] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [hydrationFailed, setHydrationFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setHydrationFailed(false);

    hydrateProductByUuid(product.uuid)
      .then((full) => {
        if (!cancelled) setHydratedRaw(full as unknown as Record<string, unknown>);
      })
      .catch((err) => {
        console.warn("Debug overlay: failed to hydrate full product, using page data", err);
        if (!cancelled) setHydrationFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [product.uuid]);

  const fullProduct = useMemo(() => {
    const raw = (hydratedRaw ?? product) as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(raw)) {
      if (value === null || value === undefined) continue;
      result[key] = value;
    }
    return result;
  }, [hydratedRaw, product]);

  return { fullProduct, loading, hydrationFailed };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ADMIN_TOOLS_BASE_URL = "https://admin.storerouter.ai/tools/catalog";

function buildAdminDebugUrl(product: Product): string | null {
  const catalog = product.catalog?.trim();
  const url = product.url?.trim();
  if (!catalog || !url) return null;

  const params = new URLSearchParams({
    tab: "product-debug",
    url,
    autoFetch: "true",
  });
  return `${ADMIN_TOOLS_BASE_URL}/${encodeURIComponent(catalog)}?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function DebugOverlayHeader({
  product,
  activeTab,
  setActiveTab,
  activeTabData,
  showRawJson,
  setShowRawJson,
  hydrationFailed,
}: {
  product: Product;
  activeTab: DebugTab;
  setActiveTab: (tab: DebugTab) => void;
  activeTabData: Record<string, unknown>;
  showRawJson: boolean;
  setShowRawJson: (fn: (v: boolean) => boolean) => void;
  hydrationFailed: boolean;
}) {
  const [copiedData, setCopiedData] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedAdmin, setCopiedAdmin] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const canViewAdminDebug = !authLoading && isInternalUser(user?.email);
  const adminDebugUrl = useMemo(
    () => (canViewAdminDebug ? buildAdminDebugUrl(product) : null),
    [canViewAdminDebug, product]
  );

  const copyToClipboard = async (text: string, setCopied: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div
      className="flex flex-shrink-0 flex-col border-b"
      style={{ borderColor: "rgba(255,255,255,0.12)" }}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3">
        <div className="flex items-center gap-3">
          <div className="rounded bg-emerald-600 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-white">
            Debug
          </div>
          {hydrationFailed && (
            <div
              className="rounded bg-amber-600/80 px-2 py-0.5 font-mono text-[10px] font-medium text-amber-100"
              title="Full product hydration failed — showing partial page data"
            >
              ⚠ partial data
            </div>
          )}
          <div className="flex gap-0.5">
            {DEBUG_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="rounded px-2.5 py-1 font-mono text-[11px] font-medium transition-colors"
                style={{
                  backgroundColor: activeTab === tab.id ? "rgba(255,255,255,0.12)" : "transparent",
                  color: activeTab === tab.id ? "#e2e8f0" : "#6b7280",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="mx-1 h-4 w-px bg-gray-700" />
          <div
            className="flex cursor-pointer rounded-full p-0.5 font-mono text-[10px] font-medium"
            style={{ backgroundColor: "rgba(255,255,255,0.08)" }}
            onClick={() => setShowRawJson((v) => !v)}
          >
            <span
              className="rounded-full px-2 py-0.5 transition-colors"
              style={{
                backgroundColor: !showRawJson ? "rgba(255,255,255,0.15)" : "transparent",
                color: !showRawJson ? "#e2e8f0" : "#6b7280",
              }}
            >
              Visual
            </span>
            <span
              className="rounded-full px-2 py-0.5 transition-colors"
              style={{
                backgroundColor: showRawJson ? "rgba(255,255,255,0.15)" : "transparent",
                color: showRawJson ? "#e2e8f0" : "#6b7280",
              }}
            >
              JSON
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {adminDebugUrl && (
            <button
              onClick={() => copyToClipboard(adminDebugUrl, setCopiedAdmin)}
              className="flex items-center gap-1.5 rounded px-2.5 py-1.5 font-mono text-[11px] font-medium text-white transition-colors"
              style={{ backgroundColor: "#7c3aed" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#6d28d9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#7c3aed";
              }}
            >
              {copiedAdmin ? <Check className="h-3 w-3" /> : <ExternalLink className="h-3 w-3" />}
              {copiedAdmin ? "Copied!" : "Admin Debug"}
            </button>
          )}
          <button
            onClick={() => copyToClipboard(product.url || "", setCopiedUrl)}
            className="flex items-center gap-1.5 rounded px-2.5 py-1.5 font-mono text-[11px] font-medium text-white transition-colors"
            style={{ backgroundColor: "#059669" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#047857";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#059669";
            }}
          >
            {copiedUrl ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            {copiedUrl ? "Copied!" : "Copy URL"}
          </button>
          <button
            onClick={() => copyToClipboard(JSON.stringify(activeTabData, null, 2), setCopiedData)}
            className="flex items-center gap-1.5 rounded px-2.5 py-1.5 font-mono text-[11px] font-medium text-white transition-colors"
            style={{ backgroundColor: "#2563eb" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#1d4ed8";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#2563eb";
            }}
          >
            {copiedData ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            {copiedData
              ? "Copied!"
              : `Copy ${activeTab === "enrichment" ? "Enrichment" : "Product"}`}
          </button>
          <div className="mx-1 hidden h-5 w-px bg-gray-700 sm:block" />
          <span className="hidden font-mono text-[10px] text-gray-500 sm:inline">
            <kbd className="rounded border border-gray-700 bg-gray-800 px-1 py-0.5">
              {typeof navigator !== "undefined" && /Mac/.test(navigator.userAgent) ? "⌘" : "Ctrl"}
              +⇧+D
            </kbd>{" "}
            to close
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Body
// ---------------------------------------------------------------------------

function DebugOverlayBody({
  activeTab,
  enrichmentData,
  productData,
  showRawJson,
  loading,
}: {
  activeTab: DebugTab;
  enrichmentData: Record<string, unknown>;
  productData: Record<string, unknown>;
  showRawJson: boolean;
  loading: boolean;
}) {
  const data = activeTab === "enrichment" ? enrichmentData : productData;
  const emptyMessage =
    activeTab === "enrichment" ? "No enrichment data available." : "No product data available.";

  return (
    <div className="flex-1 overflow-y-auto p-5">
      {loading ? (
        <p className="animate-pulse font-mono text-[11px] text-gray-500">
          Fetching full product data…
        </p>
      ) : showRawJson ? (
        <pre
          className="whitespace-pre-wrap break-words font-mono leading-relaxed"
          style={{ fontSize: "12px", color: "#a5f3ab" }}
        >
          {JSON.stringify(data, null, 2)}
        </pre>
      ) : Object.keys(data).length === 0 ? (
        <p className="italic text-gray-500">{emptyMessage}</p>
      ) : activeTab === "enrichment" ? (
        <EnrichmentRenderer data={data} />
      ) : (
        <ProductAttributesRenderer data={data} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main overlay (exported)
// ---------------------------------------------------------------------------

function DebugOverlayInner({ product }: { product: Product }) {
  const [activeTab, setActiveTab] = useState<DebugTab>("enrichment");
  const [showRawJson, setShowRawJson] = useState(false);
  const { fullProduct, loading, hydrationFailed } = useFullProduct(product);

  const enrichmentData = (fullProduct.enrichment as Record<string, unknown>) ?? {};
  const productData = useMemo(() => {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(fullProduct)) {
      if (key === "enrichment") continue;
      result[key] = value;
    }
    return result;
  }, [fullProduct]);

  const activeTabData = activeTab === "enrichment" ? enrichmentData : productData;

  return (
    <>
      {/* Desktop: overlay on the image columns (left 75%) */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-y-0 left-0 z-[9999] hidden flex-col shadow-2xl lg:flex"
        style={{ width: "75%", backgroundColor: "rgba(15, 15, 15, 0.96)" }}
      >
        <DebugOverlayHeader
          product={product}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          activeTabData={activeTabData}
          showRawJson={showRawJson}
          setShowRawJson={setShowRawJson}
          hydrationFailed={hydrationFailed}
        />
        <DebugOverlayBody
          activeTab={activeTab}
          enrichmentData={enrichmentData}
          productData={productData}
          showRawJson={showRawJson}
          loading={loading}
        />
      </motion.div>

      {/* Mobile: bottom sheet */}
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className="fixed inset-x-0 bottom-0 z-[9999] flex max-h-[70vh] flex-col rounded-t-xl shadow-2xl lg:hidden"
        style={{ backgroundColor: "rgba(15, 15, 15, 0.96)" }}
      >
        <DebugOverlayHeader
          product={product}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          activeTabData={activeTabData}
          showRawJson={showRawJson}
          setShowRawJson={setShowRawJson}
          hydrationFailed={hydrationFailed}
        />
        <DebugOverlayBody
          activeTab={activeTab}
          enrichmentData={enrichmentData}
          productData={productData}
          showRawJson={showRawJson}
          loading={loading}
        />
      </motion.div>
    </>
  );
}

export function DebugOverlay({ product }: { product: Product }) {
  return <DebugOverlayInner product={product} />;
}
